import type { TaskSnapshot } from '../../../../stores/types';
import { resolveTaskName } from '../../../../utils/utils';
import { resolvePredecessorTaskIds } from '../../../../gantt/addTaskUtils';
import {
  formatDependencyLinkToken,
  parseDependencyTokenList,
  type DependencyApiType,
} from '../../../../gantt/dependencyDisplayUtils';
import { resolveTaskWbsCode } from '../../../../gantt/taskLinkDisplayUtils';
import { validatePredecessorLink } from '../../../../gantt/predecessorValidation';

export type PredecessorTreeNode = {
  id: string;
  label: string;
  srNo: string;
  wbs: string;
  name: string;
  children: PredecessorTreeNode[];
};

export type PredecessorSelection = {
  sourceTaskId: string;
  type: DependencyApiType;
  lag: number;
  /** Display token e.g. `1.2FS+2`. */
  token: string;
  /** Resolved task name for list UI (empty if unresolved). */
  taskName: string;
  /** Sr No / WBS display code used in the token. */
  displayCode: string;
};

/** Prefer Sr No, then WBS, then name for dependency tokens. */
export function predecessorDisplayCode(task: TaskSnapshot | null | undefined): string {
  if (!task) return '';
  return resolveTaskWbsCode(task) || resolveTaskName(task) || String(task.id);
}

export function buildPredecessorTree(
  tasks: TaskSnapshot[],
  options?: { alreadySorted?: boolean },
): PredecessorTreeNode[] {
  const nodes = new Map<string, PredecessorTreeNode>();
  for (const task of tasks) {
    const id = String(task.id);
    const name = resolveTaskName(task) || id;
    const srNo = task.srNo != null ? String(task.srNo).trim() : '';
    const wbs = task.wbs != null ? String(task.wbs).trim() : '';
    const code = srNo || wbs;
    nodes.set(id, {
      id,
      label: code ? `${code} — ${name}` : name,
      srNo,
      wbs,
      name,
      children: [],
    });
  }

  const roots: PredecessorTreeNode[] = [];
  const ordered = options?.alreadySorted
    ? tasks
    : [...tasks].sort((a, b) => {
        const ao = a.outlinePosition ?? Number.MAX_SAFE_INTEGER;
        const bo = b.outlinePosition ?? Number.MAX_SAFE_INTEGER;
        if (ao !== bo) return ao - bo;
        return String(a.srNo ?? a.id).localeCompare(String(b.srNo ?? b.id), undefined, {
          numeric: true,
        });
      });

  for (const task of ordered) {
    const id = String(task.id);
    const node = nodes.get(id);
    if (!node) continue;
    const parentId = task.parentTaskId ? String(task.parentTaskId) : null;
    if (parentId && nodes.has(parentId) && parentId !== id) {
      nodes.get(parentId)!.children.push(node);
    } else {
      roots.push(node);
    }
  }
  return roots;
}

export function filterPredecessorTree(
  roots: PredecessorTreeNode[],
  searchTerm: string,
): PredecessorTreeNode[] {
  const q = searchTerm.trim().toLowerCase();
  if (!q) return roots;

  const filterNode = (node: PredecessorTreeNode): PredecessorTreeNode | null => {
    const hay = `${node.label} ${node.srNo} ${node.wbs} ${node.name} ${node.id}`.toLowerCase();
    const selfMatch = hay.includes(q);
    const kids = node.children
      .map(filterNode)
      .filter((n): n is PredecessorTreeNode => Boolean(n));
    if (!selfMatch && kids.length === 0) return null;
    return { ...node, children: kids };
  };

  return roots.map(filterNode).filter((n): n is PredecessorTreeNode => Boolean(n));
}

export function isPredecessorSelectable(
  sourceTaskId: string,
  targetTaskId: string,
  tasks: TaskSnapshot[],
  links?: Array<{ source: string | number; target: string | number }>,
): { ok: true } | { ok: false; reason: string } {
  return validatePredecessorLink(sourceTaskId, targetTaskId, tasks, links);
}

/** Resolve current predecessor input into selections with task ids. */
export function parsePredecessorSelections(
  input: string,
  tasks: TaskSnapshot[],
  targetTaskId: string,
  resolveWbsCode?: (taskId: string) => string | null,
): PredecessorSelection[] {
  const out: PredecessorSelection[] = [];
  const seen = new Set<string>();
  for (const parsed of parseDependencyTokenList(input)) {
    const [sourceId] = resolvePredecessorTaskIds(
      [parsed.taskKey],
      tasks,
      resolveWbsCode,
    ).filter((id: string) => String(id) !== String(targetTaskId));
    if (!sourceId || seen.has(String(sourceId))) continue;
    seen.add(String(sourceId));
    const task = tasks.find((t) => String(t.id) === String(sourceId));
    const code = predecessorDisplayCode(task ?? null) || parsed.taskKey;
    const taskName = task ? resolveTaskName(task) || '' : '';
    out.push({
      sourceTaskId: String(sourceId),
      type: parsed.type,
      lag: parsed.lag,
      token: formatDependencyLinkToken(code, parsed.type, parsed.lag),
      taskName,
      displayCode: code,
    });
  }
  return out;
}

export function appendPredecessorToken(currentInput: string, token: string): string {
  const t = token.trim();
  if (!t) return currentInput.trim();
  const existing = currentInput.trim();
  if (!existing) return t;
  return `${existing}, ${t}`;
}

export function removePredecessorSourceFromInput(
  currentInput: string,
  sourceTaskId: string,
  tasks: TaskSnapshot[],
  targetTaskId: string,
  resolveWbsCode?: (taskId: string) => string | null,
): string {
  return parsePredecessorSelections(currentInput, tasks, targetTaskId, resolveWbsCode)
    .filter((s) => s.sourceTaskId !== String(sourceTaskId))
    .map((s) => s.token)
    .join(', ');
}

/** Update type and/or lag for an existing predecessor source; preserves order. */
export function updatePredecessorSelectionInInput(
  currentInput: string,
  sourceTaskId: string,
  patch: { type?: DependencyApiType; lag?: number },
  tasks: TaskSnapshot[],
  targetTaskId: string,
  resolveWbsCode?: (taskId: string) => string | null,
): string {
  const source = String(sourceTaskId);
  return parsePredecessorSelections(currentInput, tasks, targetTaskId, resolveWbsCode)
    .map((s) => {
      if (s.sourceTaskId !== source) return s.token;
      const type = patch.type ?? s.type;
      const lag = patch.lag !== undefined ? patch.lag : s.lag;
      return formatDependencyLinkToken(s.displayCode, type, lag);
    })
    .join(', ');
}

/**
 * Replace one predecessor source with another, keeping type/lag from the replaced row.
 * If `newSourceTaskId` is already linked, returns currentInput unchanged (caller should warn).
 */
export function replacePredecessorSourceInInput(
  currentInput: string,
  oldSourceTaskId: string,
  newSourceTask: TaskSnapshot,
  tasks: TaskSnapshot[],
  targetTaskId: string,
  resolveWbsCode?: (taskId: string) => string | null,
): string {
  const oldId = String(oldSourceTaskId);
  const newId = String(newSourceTask.id);
  const selections = parsePredecessorSelections(
    currentInput,
    tasks,
    targetTaskId,
    resolveWbsCode,
  );
  if (selections.some((s) => s.sourceTaskId === newId && s.sourceTaskId !== oldId)) {
    return currentInput.trim();
  }
  return selections
    .map((s) => {
      if (s.sourceTaskId !== oldId) return s.token;
      return buildPredecessorTokenForTask(newSourceTask, s.type, s.lag);
    })
    .join(', ');
}

export function buildPredecessorTokenForTask(
  task: TaskSnapshot,
  type: DependencyApiType,
  lag: number,
): string {
  return formatDependencyLinkToken(predecessorDisplayCode(task), type, lag);
}
